<script setup lang="ts">
import InfiniteLoading from 'v3-infinite-loading'
import type { Article } from '@/models/content'
import { CHART_ARTICLE_TAG } from '@/constants/content'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import { logError } from '@/utils/log-error'
import { useDataVisualisationStore } from '@/stores/data-visualization'

const { $seoManager } = useNuxtApp()
const seo = $seoManager.generateSeoInfo({
  title: 'Data Visualisation',
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const { $graphqlClient } = useNuxtApp()
const dataVisualisationStore = useDataVisualisationStore()

const articles = ref<Article[]>([])
const loading = ref(true)
const excludeIds = ref<string[]>([])

const fetchContent = async () => {
  loading.value = true

  try {
    articles.value = await $graphqlClient.getArticlesByTags({
      tags: [CHART_ARTICLE_TAG],
      itemsPerQuery: 12,
    })

    dataVisualisationStore.pushArticles([...articles.value])

    excludeIds.value = articles.value.map((article) => article.id)
  } catch (e) {
    logError(e)
  }

  loading.value = false
}

onMounted(() => {
  if (dataVisualisationStore.$state.displayedArticles.length) {
    articles.value = [...dataVisualisationStore.$state.displayedArticles]
    excludeIds.value.push(...articles.value.map((article) => article.id))
    loading.value = false
  } else {
    fetchContent()
  }
})

const fetchInfiniteScrolling = async ($state?) => {
  try {
    const data = await $graphqlClient.getArticlesByTags({
      tags: [CHART_ARTICLE_TAG],
      excluded: excludeIds.value,
    })

    excludeIds.value.push(...data.map((article) => article.id))

    if (data.length) {
      articles.value.push(...data)
      dataVisualisationStore.pushArticles([...data])

      $state?.loaded()
    } else {
      $state?.complete()
    }
  } catch (e) {
    logError(e)
    $state?.error()
  }
}
</script>

<template>
  <div class="flex grow">
    <LoadingSpinner v-if="loading" wrap-screen />

    <ClientOnly v-if="!loading">
      <div class="grow">
        <div
          v-if="articles.length"
          class="mx-auto my-12 grid max-w-content-container gap-5 px-5 md:grid-cols-2 lg:gap-14"
        >
          <DataVisualisationArticleCard
            v-for="(article, index) in articles"
            :key="article.id"
            :published-date="article.publishedDate"
            :duration="article.duration"
            :title="article.title"
            :excerpt="article.excerpt"
            :pills="article.tags"
            :cover-images="{
              coverUrl: article.coverUrl,
              thumbnails: article.thumbnails,
            }"
            :slug="article.slug"
            scale-cover
            vertically-orientation
            :variant="index > 1 ? 'secondary' : 'primary'"
            class="category-card-container col-span-1 h-full"
          />
        </div>

        <InfiniteLoading
          :slots="{ complete: ' ', error: ' ' }"
          @infinite="fetchInfiniteScrolling"
        >
          <template #spinner>
            <LoadingSpinner />
          </template>
        </InfiniteLoading>
      </div>
    </ClientOnly>
  </div>
</template>
